import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Coins } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const Stat = ({ label, d, testid }) => (
  <div className="rounded-lg border border-border bg-secondary/20 px-3 py-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="mt-0.5 text-sm font-semibold">{d.edits} edit{d.edits === 1 ? "" : "s"} · {d.upscales} upscale{d.upscales === 1 ? "" : "s"}</div>
    <div className="text-xs text-muted-foreground">≈ {d.est_credits} credits (≈ ${d.est_usd.toFixed(2)})</div>
  </div>
);

function BudgetRow({ b, canEdit, onSaved }) {
  const [val, setVal] = useState(b.limit ?? "");
  const [saving, setSaving] = useState(false);
  const pct = b.limit ? Math.min(100, (b.used / b.limit) * 100) : 0;
  const tone = pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-400" : "bg-primary";
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ai-usage/budget", { monthly_credit_limit: val === "" ? null : Number(val) });
      toast.success(val === "" ? "Monthly limit removed" : `Monthly limit set to ${val} credits`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="mt-3 rounded-lg border border-border px-3 py-2.5" data-testid="ai-budget">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium">Monthly limit</span>
        <span className="text-muted-foreground" data-testid="ai-budget-status">
          {b.limit ? `≈ ${b.used} of ${b.limit} credits used · ${b.remaining > 0 ? `${b.remaining} left` : "limit reached"}` : "No limit set"}
        </span>
        {canEdit && (
          <div className="ml-auto flex items-center gap-1.5">
            <Input type="number" min="1" step="1" value={val} onChange={(e) => setVal(e.target.value)} placeholder="No limit"
              className="h-8 w-28 text-xs" data-testid="ai-budget-input" />
            <Button size="sm" variant="outline" className="h-8" onClick={save} disabled={saving} data-testid="ai-budget-save">Save</Button>
          </div>
        )}
      </div>
      {b.limit ? <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary"><div className={`h-full ${tone}`} style={{ width: `${pct}%` }} /></div> : null}
      <p className="mt-1.5 text-[11px] text-muted-foreground">You'll be asked to confirm before any photo edit or upscale that would go over the limit.</p>
    </div>
  );
}

export const AiUsagePanel = ({ refreshKey = 0, compact = false, orderId = null }) => {
  const [u, setU] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get("/ai-usage", { params: orderId ? { order_id: orderId } : {} }).then(({ data }) => setU(data)).catch(() => {});
  }, [refreshKey, tick, orderId]);
  if (!u) return null;
  return (
    <div className={compact ? "" : "rounded-xl border border-border bg-card p-5"} data-testid="ai-usage-panel">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm font-semibold">
        <Coins className="h-4 w-4 text-primary" /> AI photo usage
        <span className="text-xs font-normal text-muted-foreground">
          est. per photo: edit ≈ {u.rates.edit.credits} credits · Pro edit ≈ {u.rates.edit_pro.credits} credits · upscale ≈ {u.rates.upscale.credits} credits
        </span>
      </div>
      {u.property ? (
        <Stat label="Used on this property" d={u.property} testid="ai-usage-property" />
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Stat label="Today" d={u.today} testid="ai-usage-today" />
          <Stat label="This month" d={u.month} testid="ai-usage-month" />
          <Stat label="All time" d={u.all} testid="ai-usage-all" />
        </div>
      )}
      <BudgetRow key={u.budget.limit ?? "none"} b={u.budget} canEdit={u.can_edit_budget} onSaved={() => setTick((t) => t + 1)} />
      <p className="mt-2 text-[11px] text-muted-foreground">Estimates only. Your exact balance is in your Emergent Profile → Universal Key.</p>
    </div>
  );
};
